import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  PortalSidebar, 
  LogoutModal,
} from "@/components/PortalLayout";
import SharedFooter from "@/components/SharedFooter";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import { TeacherStatsBar } from "@/components/PortalStatsBar";
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Trophy,
  Lightbulb,
  Menu,
  MessageCircle,
  Send,
  Home,
  AlertTriangle,
  LogOut,
} from "lucide-react";

const navItems = [
  { path: "/teacher/dashboard", label: "Overview", icon: LayoutDashboard },
  { path: "/teacher/classes", label: "My Classes", icon: GraduationCap },
  { path: "/teacher/students", label: "Students", icon: Users },
  { path: "/teacher/broadcast", label: "Broadcast", icon: Send },
  { path: "/teacher/messages", label: "Messages", icon: MessageCircle },
  { path: "/teacher/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/teacher/insights", label: "Insights", icon: Lightbulb },
];

export default function TeacherLayout() {
  const [teacher, setTeacher] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle browser back button - show logout confirmation
  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setShowLogoutModal(true);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    fetchTeacherInfo();
  }, []);

  const fetchTeacherInfo = async () => {
    try {
      const response = await fetch(`${API}/teacher/me`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeacher(data.teacher);
        setSchool(data.school);
      } else {
        navigate("/teacher/login");
      }
    } catch (error) {
      navigate("/teacher/login");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/teacher/logout`, {
        method: "POST",
        credentials: "include"
      });
      toast.success("Logged out successfully");
      setShowLogoutModal(false);
      navigate("/teacher/login");
    } catch (error) {
      navigate("/teacher/login");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className={`w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4`}></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Unified Header matching landing page */}
      <UnifiedPortalHeader
        portalType="teacher"
        userName={teacher?.name}
        userSubtitle={school?.name}
        onLogout={() => setShowLogoutModal(true)}
        onMobileMenuClick={() => setSidebarOpen(true)}
        showMobileMenuButton={true}
      />
      
      {/* Teacher Stats Bar - Below header */}
      <TeacherStatsBar teacher={teacher} school={school} />

      <div className="flex flex-1">
        {/* Sidebar */}
        <PortalSidebar
          portalType="teacher"
          navItems={navItems}
          userName={teacher?.name}
          userSubtitle={`${teacher?.assignments ? Object.keys(teacher.assignments).length : 0} Grades Assigned`}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onLogout={() => setShowLogoutModal(true)}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-[calc(100vh-56px)]">
          <main className="flex-1 p-4 lg:p-6">
            <Outlet context={{ teacher, school, refreshTeacher: fetchTeacherInfo }} />
          </main>

          {/* Unified Footer matching landing page */}
          <SharedFooter />
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        open={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        onConfirm={handleLogout}
        portalType="teacher"
      />
    </div>
  );
}
